import { useAuth } from '@polvo-labs/react-auth'
import Axios from 'axios'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import Avatar from '../../../components/Avatar'
import Button from '../../../components/Button'
import Icon from '../../../components/Icon'
import Loader from '../../../components/Loader'
import Text from '../../../components/Text'
import { theme } from '../../../config/theme'
import { RoomsContext } from '../../../context/rooms-context'
import RoomType from '../../../models/Room'
import { MessageReceived, useSocket } from '../../../Socket'
import { Details, Input, LatestMessage, Room, Rooms as RoomsContainer, RoomsContainer as Container, Status } from './styles'


interface Props {
  room?: RoomType
  isAttendance?: boolean
  defaultRoom?: RoomType
  rooms: RoomType[]
  roomActions?: (room: RoomType) => JSX.Element
}

const Rooms: React.FC<Props> = ({
  room,
  defaultRoom,
  rooms,
  isAttendance = false,
  roomActions,
}) => {

  const { user } = useAuth()
  const { roomsState, roomsDispatch } = useContext(RoomsContext)

  const [name, setName] = useState<string>('')
  let timer: any = null

  const setRooms = (roomsParam: RoomType[]) => {
    if (isAttendance)
      roomsDispatch({ type: 'reload_runs', payload: { roomsRun: roomsParam } })
    else
      roomsDispatch({ type: 'reload_not_attended', payload: { roomsNotAttended: roomsParam } })
  }

  const setMoreRooms = (roomsParam: RoomType[]) => {
    if (isAttendance)
      roomsDispatch({ type: 'reload_more_runs', payload: roomsParam })
    else
      roomsDispatch({ type: 'reload_more_not_attended', payload: roomsParam })
  }

  useEffect(() => {
    roomsDispatch({ type: 'set_room', payload: undefined })
    setRooms([])
  }, [])

  const onChangeRoom = (roomParam: RoomType) => {
    roomsDispatch({ type: 'set_room', payload: { room: roomParam } })
  }

  function onReadReceipts(data: any) {
    const room = rooms.find(r => r.uuid === data.room)
    if (room && room.last_message &&
      data.messages.includes(room.last_message.id)) {
      room.last_message.is_read = true
      roomsDispatch({ type: 'update_room_in_list', payload: room })
    }
  }

  function onMessage(data: MessageReceived) {
    const room = rooms.find(r => r.uuid === data.room)
    if (room && room.uuid !== roomsState.room?.uuid) {
      const message = data.payload
      toast.info(<>
        <Text
          tag="h2"
        >{message.user?.name}</Text>
        <Text
          tag="p"
        >{message.content}</Text>
      </>)
    }
    roomsDispatch({ type: 'update_room_last_message', uui: data.room, message: data.payload })
  }

  const [meta, setMeta] = useState<any>()

  function updateTotals(total: number) {
    if (isAttendance)
      roomsDispatch({ type: 'set_rooms_run_total', payload: { roomsRunTotal: total } })
    else
      roomsDispatch({ type: 'set_rooms_not_attended_total', payload: { roomsNotAttendedTotal: total } })
  }

  let chatRoomUrl = `${process.env.REACT_APP_API_URL}/chat/rooms`
  const socket = useSocket()
  const { token } = useAuth()

  if (isAttendance) {
    chatRoomUrl = `${process.env.REACT_APP_API_URL}/chat/rooms/attendant/${user.id}`
  }

  const filterDefaultRoom = useCallback((room: RoomType) => {
    if (!defaultRoom) return true
    return room.uuid !== defaultRoom.uuid
  }, [defaultRoom])

  useEffect(() => {
    let mounted = true
    Axios({
      method: 'GET',
      url: chatRoomUrl,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then(({ data }) => {
      if (mounted) {
        const dataFiltered: RoomType[] = data.data
        setRooms(dataFiltered)
        setMeta(data.meta)
        updateTotals(data.meta.total)
        if (!matchMedia(`(max-width: ${theme.mediaQueries.home})`).matches) {
          if (isAttendance) {
            if (defaultRoom) {
              dataFiltered.map(item => {
                if (item.uuid === defaultRoom.uuid) {
                  onChangeRoom(item)
                }
                return item
              })
            } else if (!defaultRoom && isAttendance) {
              onChangeRoom(data.data[0])
            }
          }
        }
      }
    })
    return () => {
      mounted = false
    }
  }, [token, defaultRoom, filterDefaultRoom])

  useEffect(() => {
    let mounted = true
    if (mounted) {
      if (socket) {
        socket.on('message', (data: MessageReceived) => {
          onMessage(data)
        })
        socket.on('read_receipts', (data: any) => {
          onReadReceipts(data)
        })
      }
    }
    return () => {
      mounted = false
    }
  }, [socket, roomsDispatch])

  useEffect(() => {
    let mounted = true
    if (mounted) {
      if (socket) {
        rooms.forEach(room => {
          if (room) {
            socket.emit('join', {
              room: room.uuid,
            })
          }
        })
      }
    }

    return () => {
      mounted = false
    }
  }, [socket, rooms])

  const filterRooms = () => {
    Axios({
      method: 'GET',
      url: chatRoomUrl,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        name,
      },
    }).then(({ data }) => {
      setRooms(data.data)
      setMeta(data.meta)
      updateTotals(data.meta.total)
    })
  }

  useEffect(() => {
    filterRooms()
  }, [name])

  const loadMoreRooms = (page: number) => {
    Axios({
      method: 'GET',
      url: chatRoomUrl,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        name,
        page,
      },
    }).then(({ data }) => {
      const dataFiltered: RoomType[] = data.data.filter(filterDefaultRoom)
      setMoreRooms(dataFiltered)
      setMeta(data.meta)
      updateTotals(data.meta.total)
    })
  }



  const printRooms = (rooms: RoomType[]) => {
    // const Component = mapToComponent[m.type] || Text
    return rooms ? rooms.sort((a: RoomType, b: RoomType)=>{
      let bDateString = ''
      let aDateString = ''
      if(b.now)
        return 1
      if(!b.last_message || b.last_message === undefined)
      {
        return -1
      }
      else {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        bDateString = b.last_message.created_at!
      }
      if(!a.last_message || a.last_message === undefined)
      {
        return 1
      }
      else {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        aDateString = a.last_message.created_at!
      }
      const bDate = Date.parse(bDateString)
      const aDate = Date.parse(aDateString)
      return bDate -  aDate

    }).map(r => {
      const hasUnread = r.has_unread_messages || (r.last_message
        && (r.last_message.user?.id !== user.id || r.last_message.user_id !== user.id)
        && !r.last_message.is_read)

      return (
        <div
          key={r.id + '_parent'}
        >
          <Room
            hasUnreadMessages={
              hasUnread
            }
            selected={room?.uuid === r.uuid}
            key={r.id}
            onClick={() => onChangeRoom(r)}
          >
            <Avatar
              img={r.user?.avatar?.small || 'https://cdn.iptu.app/assets/img/person/placeholder.png'}
              size={48}
            />

            <Status
              online={r.receiver_status}
            />

            <Details>
              <Text
                tag="h2"
                color="darkGray"
                style={{
                  wordBreak: 'break-all',
                }}
              >
                {r.user?.name || '- Sem nome'}
              </Text>
              {r.last_message ? (
                <LatestMessage>
                  <Icon
                    name={r.last_message.type.toLowerCase()}
                  />

                  <Text
                    tag="span"
                    color="darkGray"
                    size="hint"
                  >
                    {r.last_message.type === 'TEXT' ?
                      r.last_message.content :
                      r.last_message.type === 'GEOLOCATION' ?
                        'Localização' :
                        r.last_message.upload?.name
                    }
                  </Text>

                  {r.last_message.user?.id === user.id && (
                    <Icon
                      name={r.last_message.is_read ? 'read' : 'sent'}
                    />
                  )}
                </LatestMessage>
              ) : (
                <Text
                  tag="p"
                  color="darkGray"
                  size="hint"
                >
                    Não tem mensagens
                </Text>
              )}
            </Details>
            {roomActions && (
              <div
                style={{
                  display: 'block',
                  float: 'right',
                  marginLeft: '10px',
                  width: '30px',
                }}
              >
                {roomActions(r)}
              </div>
            )}
          </Room>
        </div>
      )
    }) : (
      <Loader
        darkMode
      />
    )
  }

  return (
    <Container
      style={{ height: '100%' }}
    >
      <Input
        placeholder="Digite o nome"
        onChange={(e) => {
          const value = (e.target as any).value
          if (timer)
            clearTimeout(timer)

          timer = setTimeout(() => {
            setName(value)
          }, 500)
        }}
      />
      <RoomsContainer
        style={{ height: 'calc(100% - 47px)' }}
      >
        {printRooms(rooms)}
        {!rooms || rooms.length === 0 ? (
          <Text
            tag="h2"
            color="darkGray"
            style={{
              wordBreak: 'break-all',
            }}
          >
            Não há conversas ativas
          </Text>) : ''}
        {meta && meta.current_page < meta.last_page && (
          <Button
            label="Carregar mais"
            backgroundColor="transparent"
            fontColor="placeholder"
            block
            onClick={() => loadMoreRooms(meta.current_page + 1)}
          />
        )}
      </RoomsContainer>
    </Container>
  )
}

export default Rooms
